<template>
  <div class="selection:bg-[var(--terracotta)] selection:text-white min-h-screen bg-[var(--cream)]">
    <section class="pt-32 pb-24 px-4 md:px-6">
      <div class="max-w-7xl mx-auto">
        <div class="grid lg:grid-cols-2 gap-8 md:gap-16 items-center">
          <!-- Left side - Founder image + thumbnails -->
          <div class="order-2 lg:order-1 relative group fade-in-content">
            <Transition name="bio-image-flip" mode="out-in">
              <div
                :key="selectedFounder.id"
                class="aspect-[4/5] w-full rounded-[40%_60%_30%_70%/60%_30%_70%_40%] overflow-hidden shadow-2xl border-4 md:border-8 border-white history-image-blob bio-swap-card bio-main-blob"
                :style="{ backgroundImage: `url('${selectedFounder.img}')` }"
                role="img"
                :aria-label="`${selectedFounder.name} portrait`"
              ></div>
            </Transition>

            <div class="absolute -bottom-5 left-6 md:-bottom-8 md:left-10 flex gap-3 z-20">
              <button
                v-for="(f, idx) in founders"
                :key="f.id"
                type="button"
                class="founder-thumb-btn"
                @click="selectFounder(idx)"
                :aria-label="`Show bio for ${f.name}`"
              >
                <div
                  class="founder-thumb history-image-blob"
                  :class="{ 'founder-thumb-active': f.id === selectedFounder.id }"
                  :style="{ backgroundImage: `url('${f.img}')` }"
                ></div>
              </button>
            </div>

            <div class="absolute -top-6 -right-6 md:-top-10 md:-right-10 size-32 md:size-40 bg-[var(--terracotta)] opacity-20 blob-card -z-10"></div>
          </div>
          
          <!-- Right side - Content -->
          <div class="order-1 lg:order-2 space-y-8 fade-in-content">
            <div class="space-y-4">
              <h1 class="text-4xl md:text-5xl lg:text-6xl font-bold text-[var(--forest)] italic">Our Founders</h1>
              <div class="w-32 h-1.5 bg-[var(--terracotta)] rounded-full"></div>
            </div>
            
            <div class="bio-card space-y-5 text-base md:text-lg text-[var(--moss)] font-light leading-relaxed">
              <!-- Header row: name flips, arrow stays static -->
              <div class="space-y-1">
                <div class="flex items-center justify-between gap-4">
                  <Transition name="bio-flip" mode="out-in">
                    <h2 :key="selectedFounder.id" class="text-2xl md:text-3xl font-bold text-[var(--forest)] italic">
                      {{ selectedFounder.name }}
                    </h2>
                  </Transition>
                  <button
                    type="button"
                    class="founder-next-btn"
                    @click="nextFounder"
                    aria-label="Next founder"
                    title="Next"
                  >
                    <span class="material-symbols-outlined founder-next-icon">arrow_forward</span>
                  </button>
                </div>
              </div>

              <!-- Body flips -->
              <Transition name="bio-flip" mode="out-in">
                <div :key="selectedFounder.id" class="space-y-4">
                  <p v-if="selectedFounder.meta" class="text-sm md:text-base text-[var(--moss)] italic">
                    {{ selectedFounder.meta }}
                  </p>
                  <div class="space-y-4">
                    <p v-for="(para, i) in selectedFounder.bio" :key="i">{{ para }}</p>
                  </div>
                </div>
              </Transition>
            </div>
            
            <div class="pt-4">
              <router-link to="/" class="btn-organic bg-[var(--forest)] text-white px-10 py-4 text-lg font-bold inline-block">
                Back to Home
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { computed, onMounted, ref } from 'vue'

export default {
  name: 'BioPage',
  setup() {
    const founders = ref([
      {
        id: 'nadra',
        name: 'Nadra Aley',
        img: '/images/nadra-pic.webp',
        meta: 'Certified life coach • Founder (Safe Haven Wellness Group)',
        contact: null,
        bio: [
          'Nadra Aley is a certified life coach and the founder of Safe Haven Wellness Group. Her journey into mental health advocacy began through her own lived experience with mental health challenges, particularly involving suicide.',
          'Through the support of peer-led groups, Nadra recognized the transformative power of shared experiences. This realization inspired her to create a safe, supportive space where those navigating mental health journeys could come together for healing, learning, and growth.',
          'What began as Nadra’s personal mission has evolved into a community anchored in lived mental health experience (LMHE). Today, Safe Haven Wellness Group includes three passionate co-founders with diverse backgrounds in counseling psychology, youth advocacy, and maternal mental health.',
          'Together, they are building a platform that not only supports healing but elevates the voices of those with LMHE—ensuring they are represented in research, policy, and intervention spaces. Nadra’s vision is to bridge clinical excellence with the raw honesty of shared struggle, honoring the human behind every diagnosis.'
        ]
      },
      {
        id: 'rashid',
        name: 'Amisa Rashid Ahmed',
        img: '/images/rashid.jpeg',
        meta: 'Mental health practitioner • Neuropsychologist • Founder & Executive Director (Nivishe Foundation)',
        contact: null,
        bio: [
          'Amisa Rashid Ahmed is a mental health practitioner, neuropsychologist, and advocate whose work is expanding access to culturally grounded mental health care across Africa.',
          'As the Founder and Executive Director of Nivishe Foundation, she has led community-based interventions for underserved populations and created innovative tools to build mental health literacy—especially among young people.',
          'Her impact spans both practice and public leadership, including initiatives like Africa’s first mental health comic book and “AmisaGPT,” a Swahili/Sheng therapy-support chatbot designed for local contexts.',
          'Recognized through multiple awards and fellowships, Amisa continues to champion inclusive, Africanized, and lived-experience-informed approaches that help communities thrive emotionally and mentally.'
        ]
      },
      {
        id: 'guy',
        name: 'Eric Mbugua',
        img: '/images/guy.jpeg',
        meta: 'Professional counselor • Career mentor • Founder (Seeds of Change Initiative)',
        contact: null,
        bio: [
          'Eric Mbugua is a professional counselor, a well-versed career mentor, and a passionate advocate of societal change through community-based initiatives.',
          'His dedication to promoting positive change in society through youth empowerment led him to found the Seeds of Change Initiative—a platform focused on mentoring the next generation through structured mentorship, industry exposure, and psychoeducation.',
          'Through this work, Eric walks closely with young people as they navigate questions of identity, purpose, and livelihood, creating spaces where they can build confidence and practical skills for life and work.',
          'Eric is also a seasoned tourism professional, having specialized in marketing, public relations, and event management within the tourism industry—experience that brings a fresh perspective and a holistic view of wellbeing into his counseling and mentorship practice.'
        ]
      }
    ])

    const selectedIdx = ref(0)
    const selectedFounder = computed(() => founders.value[selectedIdx.value] || founders.value[0])

    const selectFounder = (idx) => {
      if (idx === selectedIdx.value) return
      selectedIdx.value = idx
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }

    const nextFounder = () => {
      const nextIdx = (selectedIdx.value + 1) % founders.value.length
      selectFounder(nextIdx)
    }

    const setupFadeIn = () => {
      const contentElements = document.querySelectorAll('.fade-in-content')
      
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !entry.target.classList.contains('fade-in-visible')) {
            entry.target.classList.add('fade-in-visible')
            observer.unobserve(entry.target)
          }
        })
      }, {
        rootMargin: '-10% 0px -10% 0px',
        threshold: 0.1
      })

      contentElements.forEach((content) => {
        observer.observe(content)
      })
    }

    onMounted(() => {
      setupFadeIn()
    })

    return {
      founders,
      selectedFounder,
      selectFounder,
      nextFounder
    }
  }
}
</script>

<style scoped>
.fade-in-content {
  opacity: 0;
  transform: translateY(30px);
  transition: opacity 0.8s ease-out, transform 0.8s ease-out;
}

.fade-in-content.fade-in-visible {
  opacity: 1;
  transform: translateY(0);
}

/* History image blob hover effect */
.history-image-blob {
  transition: border-radius 0.5s ease-in-out;
}

/* Only morph the MAIN image when it is hovered (don't change thumbnails) */
.bio-main-blob:hover {
  border-radius: 50% 50% 45% 55% / 60% 60% 40% 40% !important;
}

.bio-swap-card {
  background-size: cover;
  background-position: center;
}

.founder-next-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  color: var(--forest);
  background: transparent;
  border: none;
  transition: transform 150ms ease, opacity 150ms ease;
}

.founder-next-btn:hover {
  transform: translateX(2px);
  opacity: 0.85;
}

.founder-next-btn:active {
  transform: translateX(1px) scale(0.98);
}

.founder-next-btn:focus-visible {
  outline: 3px solid rgba(45, 70, 53, 0.6);
  outline-offset: 4px;
}

.founder-next-icon {
  font-size: 22px;
  line-height: 1;
}

.founder-thumb-btn {
  display: inline-flex;
  padding: 0;
}

.founder-thumb-btn:focus-visible {
  outline: 3px solid rgba(45, 70, 53, 0.6);
  outline-offset: 4px;
}

.founder-thumb {
  width: 64px;
  height: 64px;
  border: 3px solid rgba(255, 255, 255, 0.95);
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.18);
  background-size: cover;
  background-position: center;
  /* Default rugged/blob shape (so it's not square at rest) */
  border-radius: 35% 65% 25% 75% / 60% 25% 75% 40%;
  overflow: hidden;
  transform: rotate(-3deg);
  transition: transform 200ms ease, box-shadow 200ms ease;
}

.founder-thumb-btn:hover .founder-thumb {
  /* Thumbnail morph happens only on its own hover */
  border-radius: 50% 50% 45% 55% / 60% 60% 40% 40% !important;
}

.founder-thumb-btn:hover .founder-thumb {
  transform: rotate(-8deg) scale(1.05);
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.22);
}

.founder-thumb-active {
  box-shadow: 0 18px 48px rgba(0, 0, 0, 0.26);
  transform: rotate(-2deg) scale(1.06);
}

/* Smooth flip transitions for bio content + image */
.bio-flip-enter-active,
.bio-flip-leave-active,
.bio-image-flip-enter-active,
.bio-image-flip-leave-active {
  transition: transform 420ms cubic-bezier(0.22, 1, 0.36, 1), opacity 420ms ease;
  transform-style: preserve-3d;
}

.bio-flip-enter-from,
.bio-image-flip-enter-from {
  opacity: 0;
  transform: perspective(900px) rotateY(10deg) translateY(6px);
}

.bio-flip-leave-to,
.bio-image-flip-leave-to {
  opacity: 0;
  transform: perspective(900px) rotateY(-10deg) translateY(-6px);
}

.bio-flip-enter-to,
.bio-flip-leave-from,
.bio-image-flip-enter-to,
.bio-image-flip-leave-from {
  opacity: 1;
  transform: perspective(900px) rotateY(0deg) translateY(0);
}
</style>
